import React from 'react';
import {Select} from 'src/v2/components/common';
import {getStore} from 'src/v2/state/store';
import {
  getNetworkNameForLivemode,
  getNetworkIcon,
} from 'src/components/Wallet/constants';
import type {SupportedNetwork} from '@stripe-internal/wallet-connect-wrapper';
import {GaslessBadge} from 'src/v2/components/walletConnect/GaslessBadge';
import {useSessionContext} from 'src/state/usePayinSessionContext';

import {message, useIntl} from '@sail/intl';

const messages = {
  networkLabel: message({
    id: 'crypto.payins.v2.network_switch_selector.label',
    description: 'Label for the network selector',
    defaultMessage: 'Network',
  }),
};

interface NetworkSwitchSelectorProps {
  supportedNetworks: SupportedNetwork[];
  selectedNetworkId: SupportedNetwork;
  onNetworkSelect: (networkId: SupportedNetwork) => void;
}

export const NetworkSwitchSelector: React.FC<NetworkSwitchSelectorProps> = ({
  supportedNetworks,
  selectedNetworkId,
  onNetworkSelect,
}) => {
  const intl = useIntl();
  const store = getStore();
  const {mode} = useSessionContext();
  const livemode = store.useSelector((state) => state.livemode);
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const enableGaslessV2 = store.useSelector(
    (state) => state.featureFlags.enable_gasless_v2,
  );
  const spenderContractAddresses = store.useSelector(
    (state) => state.spenderContractAddresses,
  );
  const setupFutureUsage = store.useSelector((state) => state.setupFutureUsage);

  // Create combined network list: supportedNetworks + activeWallet.network (if different)
  const allNetworks = [...supportedNetworks];
  if (
    activeWallet?.network &&
    !supportedNetworks.includes(activeWallet.network)
  ) {
    allNetworks.push(activeWallet.network);
  }

  const spenderKeys = enableGaslessV2
    ? Object.keys(spenderContractAddresses)
    : [];

  // setup sessions and SFU are inherently gasless, no need for extra promotion
  // smart contract wallets don't support gasless, so hide the badge
  const shouldShowGaslessBadge =
    mode !== 'setup' &&
    !setupFutureUsage &&
    !activeWallet?.isSmartContractWallet;

  const isNetworkGasless = (networkId: SupportedNetwork) =>
    shouldShowGaslessBadge &&
    spenderKeys.some((key) => key.endsWith(`.${networkId}`));

  const networks = allNetworks.map((networkId: SupportedNetwork) => ({
    id: networkId,
    label: getNetworkNameForLivemode(networkId, livemode),
    icon: getNetworkIcon(networkId),
    description: isNetworkGasless(networkId) ? <GaslessBadge /> : undefined,
  }));

  const disabledNetworks = allNetworks.filter(
    (networkId) => !supportedNetworks.includes(networkId),
  );

  return (
    <Select
      items={networks}
      selectedItemId={selectedNetworkId}
      onItemSelect={(networkId) => {
        onNetworkSelect(networkId as SupportedNetwork);
      }}
      disabledKeys={disabledNetworks}
      label={intl.formatMessage(messages.networkLabel)}
    />
  );
};
